/**
 * Cluster access for the reporting login gate (UX only — the Lambda resolver in
 * infrastructure/core/lambda/authorization.py is the security boundary and
 * applies the same rule independently).
 *
 * Rule (no stored membership): cluster C reaches school S iff some Survey has
 * { dataClientGroup: S, accessClientGroups: C }. A cluster user is a user
 * approved (status 'approved', removed != true) in a ClientGroup C having
 * permissions 'showClusterSurveys'. Ids in Surveys are STRINGS.
 *
 * Only call this when evaluateLoginStatus would otherwise return 'register'
 * (resolveLoginDecision below does that), so ordinary members never pay for it.
 */
import { evaluateLoginStatus } from './loginStatusGate.js';

/** Extended-JSON-safe string form of a Mongo _id ({$oid} or string). */
function idString(id) {
  if (id && typeof id === 'object' && typeof id.$oid === 'string') return id.$oid;
  return id == null ? '' : String(id);
}

/**
 * Is `email` an approved cluster user whose cluster reaches `schoolId`?
 * Never throws: any failure (network, denied read, no token) => false, which
 * leaves today's 'register' behaviour unchanged.
 *
 * @param {object} params
 * @param {object} params.db            DatabaseService (auth.db)
 * @param {string} params.email
 * @param {object|string} params.schoolId  the school ClientGroup _id
 * @param {() => boolean} [params.isTokenReady] must return true before any DB
 *        call is made. The Surveys read is a sensitive op, so a tokenless call
 *        would trip the Lambda ST4S softening; skip rather than fire it.
 * @returns {Promise<boolean>}
 */
export async function hasClusterAccess({ db, email, schoolId, isTokenReady } = {}) {
  const normalisedEmail = (email || '').trim().toLowerCase();
  const school = idString(schoolId);
  if (!db || !normalisedEmail || !school) return false;
  if (typeof isTokenReady === 'function' && !isTokenReady()) return false;

  try {
    const clusters = await db.collection('ClientGroups').find(
      {
        permissions: 'showClusterSurveys',
        users: { $elemMatch: { email: normalisedEmail, status: 'approved', removed: { $ne: true } } }
      },
      { _id: 1 }
    );
    const clusterIds = (clusters || []).map(c => idString(c._id)).filter(Boolean)
      .filter(id => id !== school);
    if (clusterIds.length === 0) return false;

    const hit = await db.collection('Surveys').find(
      { dataClientGroup: school, accessClientGroups: { $in: clusterIds } },
      { _id: 1, limit: 1 }
    );
    return Array.isArray(hit) && hit.length > 0;
  } catch (err) {
    console.warn('Cluster access check failed; treating as no access:', err && err.message);
    return false;
  }
}

/**
 * The single login-completion decision used by ALL five call sites (LoginPage,
 * MFAVerifyPage, MFASetupPage, NewPasswordPage, App restore) so fresh login and
 * restored sessions cannot disagree. Runs the pure gate first; only when it says
 * 'register' does it consult hasClusterAccess and re-evaluate.
 *
 * @returns {Promise<{ action: string, message?: string, clusterAccess: boolean }>}
 */
export async function resolveLoginDecision({ email, groups, userData, db, isTokenReady } = {}) {
  const base = evaluateLoginStatus({ email, groups, userData });
  if (base.action !== 'register') return { ...base, clusterAccess: false };

  const clusterAccess = await hasClusterAccess({
    db, email, schoolId: userData && userData._id, isTokenReady
  });
  if (!clusterAccess) return { ...base, clusterAccess: false };
  return { ...evaluateLoginStatus({ email, groups, userData, clusterAccess: true }), clusterAccess: true };
}
